import { useEffect, useMemo, useState } from 'react'
import useFetch from '../../hooks/useFetch'
import useAuth from '../../hooks/useAuth'
import { urlApi } from '../../config'
import ReviewCard from './ReviewCard'
import StatsSidebar from './StatsSidebar'
import useReviewCardCache from './useReviewCardCache'
import LobbyInfo from '../../common/LobbyInfo'
import './payout-review.css'

function SkeletonCard() {
  return (
    <div className='pr-card bg-white dark:bg-dk-surface rounded-[28px] ring-1 ring-warm_border dark:ring-dk-border w-full max-w-[30rem] mx-auto p-7 flex flex-col gap-5'>
      <div className='flex items-center gap-3'>
        <div className='pr-skeleton w-12 h-12 rounded-full' />
        <div className='flex-1 flex flex-col gap-2'>
          <div className='pr-skeleton h-3.5 w-36 rounded-full' />
          <div className='pr-skeleton h-3 w-24 rounded-full' />
        </div>
      </div>
      <div className='pr-skeleton h-12 w-48 rounded-2xl' />
      <div className='flex flex-col gap-2'>
        <div className='pr-skeleton h-9 w-full rounded-xl' />
        <div className='pr-skeleton h-9 w-full rounded-xl' />
      </div>
      <div className='flex gap-3 pt-2'>
        <div className='pr-skeleton h-12 flex-1 rounded-2xl' />
        <div className='pr-skeleton h-12 flex-1 rounded-2xl' />
      </div>
    </div>
  )
}

function CardLoader({ entry, submitting, onApprove, onReject, onOpenLobby, onSkip }) {
  if (!entry || entry.status === 'loading') return <SkeletonCard />
  if (entry.status === 'error' || !entry.data || entry.data.status) {
    return (
      <div className='pr-card bg-white dark:bg-dk-surface rounded-[28px] ring-1 ring-warm_border dark:ring-dk-border w-full max-w-[30rem] mx-auto p-8 text-center text-ink/70 dark:text-gray-100 font-medium'>
        No se pudo cargar este payout
      </div>
    )
  }
  return (
    <ReviewCard
      card={entry.data}
      submitting={submitting}
      onApprove={onApprove}
      onReject={onReject}
      onOpenLobby={onOpenLobby}
      onSkip={onSkip}
    />
  )
}

function LobbyModal({ lobbyId, onClose }) {
  const { loading, data } = useFetch(`${urlApi}/admin/lobbies/${lobbyId}`)
  if (loading) return null
  if (!data || data.status || !data._id) return null
  return <LobbyInfo lobby={data} setSelected={() => onClose()} />
}

function StageMessage({ emoji, title, subtitle }) {
  return (
    <div className='flex-1 flex flex-col items-center justify-center text-center px-6 py-16'>
      <div className='text-6xl mb-5'>{emoji}</div>
      <h2 className='font-display font-bold text-2xl text-ink dark:text-gray-100'>{title}</h2>
      {subtitle && <p className='text-warm_gray dark:text-gray-400 mt-2 max-w-xs'>{subtitle}</p>}
    </div>
  )
}

function CurrencyToggle({ currency, onChange }) {
  return (
    <div className='inline-flex items-center rounded-full bg-white dark:bg-dk-surface p-1 ring-1 ring-warm_border dark:ring-dk-border shadow-sm'>
      {['UYU', 'ARS'].map(c => (
        <button
          key={c}
          onClick={() => onChange(c)}
          aria-pressed={currency === c}
          className={`px-4 py-1.5 rounded-full text-sm font-display font-bold tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/50 ${
            currency === c ? 'bg-coral text-white shadow' : 'text-ink/60 dark:text-gray-400 hover:text-ink dark:hover:text-white'
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  )
}

function QueueView({ currency, getAccessTokenSilently }) {
  const { loading, data } = useFetch(`${urlApi}/admin/driver-payments/review-queue?currency=${currency}`)
  const [queue, setQueue] = useState([])
  const [idx, setIdx] = useState(0)
  const [leaving, setLeaving] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [openLobbyId, setOpenLobbyId] = useState(null)
  const { prefetchFrom, getEntry } = useReviewCardCache()

  useEffect(() => {
    if (Array.isArray(data)) setQueue(data)
  }, [data])

  const queueIds = useMemo(() => queue.map(item => item._id), [queue])

  useEffect(() => {
    prefetchFrom(queueIds, idx)
  }, [queueIds, idx, prefetchFrom])

  const current = queue[idx]
  const currentEntry = current ? getEntry(current._id) : undefined

  const decide = async (decision, reason, flags) => {
    if (!current || submitting) return
    setSubmitting(true)
    setError(null)
    setLeaving(decision === 'approve' ? 'right' : 'left')
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${urlApi}/admin/driver-payments/review/${current._id}/decision`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(typeof token === 'string' && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ decision, reason, flags })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (e) {
      console.error(e)
      setLeaving(null)
      setSubmitting(false)
      setError('No se pudo guardar la decisión. La tarjeta sigue en la cola, reintentá.')
      return
    }
    setTimeout(() => {
      setLeaving(null)
      setSubmitting(false)
      setIdx(i => i + 1)
    }, 220)
  }

  const skip = () => {
    if (!current || submitting) return
    setError(null)
    setLeaving('skip')
    setTimeout(() => {
      setLeaving(null)
      setIdx(i => i + 1)
    }, 220)
  }

  if (loading) {
    return (
      <div className='flex-1 flex items-start justify-center pt-12 px-6'>
        <SkeletonCard />
      </div>
    )
  }
  if (!queue.length) {
    return <StageMessage emoji='✅' title='No hay payouts para revisar' subtitle='Cuando entre un pago nuevo aparecerá acá para revisarlo.' />
  }
  if (idx >= queue.length) {
    return (
      <StageMessage
        emoji='🎉'
        title='Revisaste todos los payouts'
        subtitle={`${queue.length} ${queue.length === 1 ? 'pago revisado' : 'pagos revisados'} en esta tanda. Buen trabajo.`}
      />
    )
  }

  const slideClass =
    leaving === 'right'
      ? 'translate-x-[140%] rotate-6 opacity-0'
      : leaving === 'left'
      ? '-translate-x-[140%] -rotate-6 opacity-0'
      : leaving === 'skip'
      ? '-translate-y-8 scale-[.97] opacity-0'
      : 'translate-x-0 rotate-0 opacity-100'

  return (
    <>
      <div className='flex-1 flex flex-col lg:flex-row gap-8 p-6 lg:p-8 overflow-auto'>
        <div className='flex-1 flex flex-col items-center'>
          <div className='w-full max-w-[30rem] mb-5'>
            <div className='flex items-center justify-between text-xs text-ink/55 dark:text-gray-400 font-medium mb-1.5'>
              <span>Payout <span className='font-display font-bold text-ink dark:text-gray-100'>{idx + 1}</span> de {queue.length}</span>
              <span className='font-display font-bold tracking-wide text-coral'>{currency}</span>
            </div>
            <div className='h-1.5 rounded-full bg-ink/10 dark:bg-dk-raised overflow-hidden'>
              <div
                className='h-full bg-coral rounded-full transition-all duration-300'
                style={{ width: `${(idx / queue.length) * 100}%` }}
              />
            </div>
          </div>

          {error && (
            <div className='w-full max-w-[30rem] mb-3 rounded-2xl bg-coral/10 ring-1 ring-coral/30 px-4 py-2.5 text-sm font-medium text-coral_deep'>
              {error}
            </div>
          )}

          <div className='relative w-full max-w-[30rem]'>
            <div className='absolute inset-0 translate-y-2.5 scale-[.965] rounded-[28px] bg-white/70 dark:bg-dk-surface/70 ring-1 ring-warm_border dark:ring-dk-border' />
            <div className='absolute inset-0 translate-y-5 scale-[.93] rounded-[28px] bg-white/40 dark:bg-dk-surface/40 ring-1 ring-warm_border dark:ring-dk-border' />
            <div
              key={current._id}
              className={`pr-swipe pr-card-in relative motion-reduce:transition-none transition-all duration-200 ease-out ${slideClass}`}
            >
              <CardLoader
                entry={currentEntry}
                submitting={submitting}
                onApprove={flags => decide('approve', undefined, flags)}
                onReject={(reason, flags) => decide('reject', reason, flags)}
                onOpenLobby={setOpenLobbyId}
                onSkip={skip}
              />
            </div>
          </div>
        </div>

        <div className='lg:w-72 shrink-0'>
          <StatsSidebar currency={currency} />
        </div>
      </div>
      {openLobbyId && <LobbyModal lobbyId={openLobbyId} onClose={() => setOpenLobbyId(null)} />}
    </>
  )
}

export default function PayoutReview() {
  const { getAccessTokenSilently } = useAuth()
  const [currency, setCurrency] = useState(() => localStorage.getItem('payout_review_currency') || 'ARS')

  const handleCurrencyChange = val => {
    localStorage.setItem('payout_review_currency', val)
    setCurrency(val)
  }

  return (
    <div className='pr-stage h-full flex flex-col overflow-hidden text-ink dark:text-gray-100'>
      <div className='flex items-center justify-between gap-4 px-6 lg:px-8 pt-6 shrink-0'>
        <div>
          <p className='text-coral text-[11px] font-display font-bold tracking-[0.2em] uppercase'>Viatik · Cobros</p>
          <h1 className='font-display font-bold text-2xl lg:text-3xl text-ink dark:text-gray-100'>Revisión de payouts</h1>
        </div>
        <CurrencyToggle currency={currency} onChange={handleCurrencyChange} />
      </div>
      <QueueView key={currency} currency={currency} getAccessTokenSilently={getAccessTokenSilently} />
    </div>
  )
}
